import React, { useEffect, useState } from "react";
import styles from "./Settings.module.css";
import { FaPlay } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import { IoIosCreate, IoMdLogIn } from "react-icons/io";
import { IoQrCodeOutline } from "react-icons/io5";
import { ImHome3 } from "react-icons/im";
import { BiSolidMoviePlay } from "react-icons/bi";
import { SiSteelseries } from "react-icons/si";
import { MdOutlineLiveTv } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";
import { getSettingsSubscription, patchSettingsField } from "../../api/HttpApis";
import { checkAiAvailable } from "../../services/ai/aiAvailability";

const Settings = () => {
  const [settingsConfig, setSettingsConfig] = useState({
    roku_pay_api_key: null,
    amazon_iap_secret: null,
    google_play_billing_key: null,
    enable_subscription: false,
    enable_ads: false,
    enable_qrlogin: false,
    enable_login: false,
    enable_home: false,
    enable_creator: false,
    enable_movie: false,
    enable_series: false,
    enable_live: false,
    enable_ai: false,
  });
  // Why the AI toggle refused to turn on (empty = nothing to say).
  const [aiCheckMessage, setAiCheckMessage] = useState("");

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getSettingsSubscription();
        const config = response?.data;

        setSettingsConfig({
          roku_pay_api_key: config?.roku_pay_api_key ?? null,
          amazon_iap_secret: config?.amazon_iap_secret ?? null,
          google_play_billing_key: config?.google_play_billing_key ?? null,
          enable_subscription: config?.enable_subscription ?? false,
          enable_ads: config?.enable_ads ?? false,
          enable_qrlogin: config?.enable_qrlogin ?? false,
          enable_login: config?.enable_login ?? false,
          enable_home: config?.enable_home ?? false,
          enable_creator: config?.enable_creator ?? false,
          enable_movie: config?.enable_movie ?? false,
          enable_series: config?.enable_series ?? false,
          enable_live: config?.enable_live ?? false,
          enable_ai: config?.enable_ai ?? false,
        });
      } catch (error) {
        console.error("Failed to fetch settings config:", error);
      }
    };

    fetchConfig();
  }, []);

  const handleToggle = async (key) => {
    const updatedValue = !settingsConfig[key];

    // Turning AI ON requires a reachable analyze server with a Gemini key —
    // otherwise the operator would get AI buttons that fail on click. Switching
    // it OFF is always allowed, so a broken AI server can still be disabled.
    if (key === "enable_ai" && updatedValue) {
      setAiCheckMessage("Checking AI server…");
      const { available, reason } = await checkAiAvailable();
      if (!available) {
        setAiCheckMessage(`${reason} AI cannot be enabled.`);
        return;
      }
      setAiCheckMessage("");
    }

    setSettingsConfig((prev) => ({
      ...prev,
      [key]: updatedValue,
    }));

    try {
      await patchSettingsField({
        field: key,
        value: updatedValue,
      });
    } catch (error) {
      console.error(`Failed to update ${key}:`, error);

      setSettingsConfig((prev) => ({
        ...prev,
        [key]: !updatedValue,
      }));
    }
  };

  return (
    <div className={styles.innerContainer}>
      <h2 className={styles.title}>Settings</h2>
      <div className={styles.adCards}>
        {/* Enable Subscription */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <MdSubscriptions className={styles.icon} />
              <div>
                <h4>Enable Subscription</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_subscription}
                  onChange={() => handleToggle("enable_subscription")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Ads */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <FaPlay className={styles.icon} />
              <div>
                <h4>Enable Ads</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_ads}
                  onChange={() => handleToggle("enable_ads")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable QR Login */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <IoQrCodeOutline className={styles.icon} />
              <div>
                <h4>Enable QR Login</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_qrlogin}
                  onChange={() => handleToggle("enable_qrlogin")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Login */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <IoMdLogIn className={styles.icon} />
              <div>
                <h4>Enable Login</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_login}
                  onChange={() => handleToggle("enable_login")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Home */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <ImHome3 className={styles.icon} />
              <div>
                <h4>Enable Home</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_home}
                  onChange={() => handleToggle("enable_home")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Creator */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <IoIosCreate className={styles.icon} />
              <div>
                <h4>Enable Creator</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_creator}
                  onChange={() => handleToggle("enable_creator")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Movie */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <BiSolidMoviePlay className={styles.icon} />
              <div>
                <h4>Enable Movie</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_movie}
                  onChange={() => handleToggle("enable_movie")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Series */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <SiSteelseries className={styles.icon} />
              <div>
                <h4>Enable Series</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_series}
                  onChange={() => handleToggle("enable_series")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable Live */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <MdOutlineLiveTv className={styles.icon} />
              <div>
                <h4>Enable Live</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_live}
                  onChange={() => handleToggle("enable_live")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enable AI — master switch for the whole AI interface (AI Studio,
            metadata, subtitles, dubbing, trailers, scrubbing previews).
            Requires a Gemini key + a running analyze server; leave it off for
            clients that have neither. Takes effect on reload. */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>
              <HiSparkles className={styles.icon} />
              <div>
                <h4>Enable AI</h4>
              </div>
            </div>
            <div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settingsConfig.enable_ai}
                  onChange={() => handleToggle("enable_ai")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
          {aiCheckMessage && (
            <p style={{ margin: "8px 0 0", fontSize: 12, opacity: 0.75 }}>
              {aiCheckMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
